/**
 * The dashboard's staff-management endpoints, typed — mirrors `locations/api.ts` and `content/api.ts`.
 *
 * Every route here is admin only on the server (`apps/api/src/staff/admin-routes.ts`); this module
 * does not re-check that, it only names the paths and the contracts. The reads take no CSRF token —
 * they are safe methods — and every write forwards the one the session already holds, exactly as the
 * other feature APIs do. No response type carries a password: a staff row on the wire is
 * `staffMemberSchema` (id, nombre, email, rol), and a reset answers with the same shape.
 */
import {
  deleteResultSchema,
  staffListSchema,
  staffMemberSchema,
  type CreateStaffRequest,
  type DeleteResult,
  type ResetStaffPasswordRequest,
  type StaffList,
  type StaffMember,
  type UpdateStaffRoleRequest,
} from '@apuestapr/contracts';

import type { ApiClient, ApiResult } from '../api/client.ts';

export const staffListPath = '/api/admin/staff';
const staffPath = (id: number): string => `${staffListPath}/${String(id)}`;
const staffRolePath = (id: number): string => `${staffPath(id)}/role`;
const staffPasswordPath = (id: number): string => `${staffPath(id)}/password`;

export interface StaffApi {
  readonly list: () => Promise<ApiResult<StaffList>>;
  readonly create: (
    input: CreateStaffRequest,
    csrfToken: string,
  ) => Promise<ApiResult<StaffMember>>;
  readonly updateRole: (
    id: number,
    input: UpdateStaffRoleRequest,
    csrfToken: string,
  ) => Promise<ApiResult<StaffMember>>;
  readonly resetPassword: (
    id: number,
    input: ResetStaffPasswordRequest,
    csrfToken: string,
  ) => Promise<ApiResult<StaffMember>>;
  readonly remove: (id: number, csrfToken: string) => Promise<ApiResult<DeleteResult>>;
}

export const createStaffApi = (client: ApiClient): StaffApi => ({
  list: () => client.get(staffListPath, staffListSchema),
  create: (input, csrfToken) =>
    client.send('POST', staffListPath, staffMemberSchema, { body: input, csrfToken }),
  updateRole: (id, input, csrfToken) =>
    client.send('PATCH', staffRolePath(id), staffMemberSchema, { body: input, csrfToken }),
  resetPassword: (id, input, csrfToken) =>
    client.send('POST', staffPasswordPath(id), staffMemberSchema, { body: input, csrfToken }),
  remove: (id, csrfToken) =>
    client.send('DELETE', staffPath(id), deleteResultSchema, { csrfToken }),
});
